import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hydrateInitialConfigFromProfile } from "../redux/reducer/initialConfigSlice";
import { getUserInfo, resetUserInfoThrottle } from "../redux/actions/userActions";
import { getEntitlements } from "../redux/actions/subscriptionsActions";
import { hydrateGoalsCatalogFromApi } from "../services/onboardingProfile";
import { initRevenueCat, subscribeToEntitlementUpdates } from "./RevenueCat";
import { safeGetItem } from "../utils/safeStorage";

export function useHydrateOnboardingProfile() {
  const dispatch = useDispatch();

  const hasToken = !!safeGetItem("adoc_t");

  const me = useSelector((s) => s.user?.user);
  const subscriber = useSelector((s) => s.user?.master_club_subscriber);
  const hydrated = useSelector((s) => s.initialConfig?.hydratedFromProfile);

  const agreementsLoaded = useSelector((s) => Array.isArray(s.user?.agreements));

  const userLoading = useSelector(
    (s) => !!(s.user?.loading || s.user?.isLoading || s.user?.fetching)
  );

  // optional: if you added these keys in initialState, use them as “loaded” check
  const goalsCatalogLoaded = useSelector(
    (s) =>
      Array.isArray(s.initialConfig?.nutritionalGoalsList) &&
      s.initialConfig.nutritionalGoalsList.length > 0
  );

  const didFetchGoalsRef = useRef(false);
  const didFetchUserRef = useRef(false);
  const didInitRcRef = useRef(false);
  const didColdRevalidateRef = useRef(false);

  // Cold-boot revalidation: regardless of cached `adoc_p` profile, always
  // refetch the user's authoritative state from the backend on first mount
  // when a token is present. Without this, a cached premium subscription
  // status persists in localStorage / Redux even after the user has cancelled
  // (or after Apple/Google's auto-expiration), because the `getUserInfo`
  // dispatch below is gated on "cached state missing" — which is false when
  // hydrated from `adoc_p`. Web doesn't hit this because it has no cached
  // profile. The throttle is reset so this fetch is never silently skipped.
  useEffect(() => {
    if (!hasToken || didColdRevalidateRef.current) return;
    didColdRevalidateRef.current = true;
    resetUserInfoThrottle();
    dispatch(getUserInfo());
    dispatch(getEntitlements());
  }, [hasToken, dispatch]);

  // Initialize the RevenueCat SDK once we know the user id. Runs only on native
  // (the service no-ops on web). Idempotent — re-runs across logout/login because
  // the user id changes, which clears the ref via the dependency on me?.id.
  //
  // After configure() resolves, subscribe to RC's customer-info update stream so
  // entitlement flips (CANCELLATION → access still active until expiration; then
  // EXPIRATION → access revoked) propagate to the app within seconds. Without
  // this, the app only learns about cancellations on the next cold start.
  useEffect(() => {
    if (!hasToken || !me?.id || didInitRcRef.current) return;
    didInitRcRef.current = true;

    let unsubscribe = () => {};
    initRevenueCat(me.id)
      .then(async () => {
        unsubscribe = await subscribeToEntitlementUpdates(() => {
          // RC observed an entitlement flip — bypass the throttle and refetch
          // the user's authoritative state from the backend.
          resetUserInfoThrottle();
          dispatch(getUserInfo());
          dispatch(getEntitlements());
        });
      })
      .catch(() => {
        // initRevenueCat already logs internally; allow retry on next mount.
        didInitRcRef.current = false;
      });

    return () => {
      try {
        unsubscribe();
      } catch (_) {}
    };
  }, [hasToken, me?.id, dispatch]);

  useEffect(() => {
    const run = async () => {
      // 1) If user/subscriber already exists, hydrate initialConfig immediately
      if (!hydrated && (me || subscriber)) {
        dispatch(hydrateInitialConfigFromProfile({ user: me, subscriber }));
      }

      // 2) Refresh user profile/agreements if logged in but missing in store (covers localStorage clears)
      if (
        hasToken &&
        !userLoading &&
        !didFetchUserRef.current &&
        ((!me && !subscriber) || !agreementsLoaded)
      ) {
        didFetchUserRef.current = true;
        dispatch(getUserInfo());
      }

      // 3) Fetch goals catalog once (needs token)
      if (
        hasToken &&
        !userLoading &&
        !goalsCatalogLoaded &&
        !didFetchGoalsRef.current
      ) {
        didFetchGoalsRef.current = true;
        try {
          await hydrateGoalsCatalogFromApi(dispatch);
        } catch {
          // keep “true” to avoid spamming; if you want retries, reset this to false on error.
        }
      }
    };

    run();
  }, [
    dispatch,
    hydrated,
    me,
    subscriber,
    hasToken,
    userLoading,
    goalsCatalogLoaded,
    agreementsLoaded,
  ]);
}
